/**
 * "El servicio está suspendido" (T049; spec FR-023; textos PRD §72 desde 006 · T054).
 *
 * Destino de `fermin:company-suspended` (`423 COMPANY_SUSPENDED`) y de Inicio cuando `GET /me`
 * devuelve la empresa `SUSPENDED`. No es un problema del trabajador ni de su teléfono: su cuenta y
 * su credencial siguen intactas, así que la única acción es reintentar cuando la empresa regularice
 * su situación. "Reintentar" invalida `GET /me` y vuelve a Inicio, que decide de nuevo.
 *
 * "Ver mi asistencia" (006 · T055, R-12) lleva a `/asistencia`: el realm exime esa lectura del
 * `423 COMPANY_SUSPENDED` desde T006, así que el historial y el perfil siguen siendo consultables
 * aunque la empresa esté suspendida.
 */
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Screen } from '../../components/ui/screen';
import { t } from '../../i18n';

import { STAFF_ME_QUERY_KEY } from './api';

export interface SuspendedScreenProps {
  /** Se llama tras invalidar `GET /me`: normalmente vuelve a `/`. */
  onRetry: () => void;
  /** Lleva a `/asistencia` (006 · T055): historial de solo lectura mientras dure la suspensión. */
  onViewHistory: () => void;
}

export function SuspendedScreen({ onRetry, onViewHistory }: SuspendedScreenProps) {
  const queryClient = useQueryClient();

  return (
    <Screen
      data-testid="access-suspended"
      align="center"
      title={t('access.suspended.title')}
      lead={t('access.suspended.description')}
      footer={
        <>
          <Button
            icon={<RefreshCw aria-hidden="true" size={20} />}
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: STAFF_ME_QUERY_KEY });
              onRetry();
            }}
          >
            {t('common.buttons.retry')}
          </Button>
          <Button
            data-testid="access-suspended-history"
            variant="secondary"
            onClick={onViewHistory}
          >
            {t('access.suspended.history')}
          </Button>
        </>
      }
    />
  );
}
